import axios from 'axios';
import { AuthActionCreators, IGet } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { useEffect, useState } from 'react';

export type NetworkEmailLogSortColumn = 'date' | 'subject' | 'sender';

export interface INetworkEmailLogFilteredListRequest {
    sender?: string;
    search?: string;
    centerId?: number;
    sortBy?: NetworkEmailLogSortColumn;
    sortDescending?: boolean;
    page?: number;
    pageSize?: number;
}

export interface INetworkEmailLogListItem {
    id: number;
    sentDate: string | null;
    sender: string;
    subject: string;
}

interface IRetVal {
    loading: boolean;
    error: string;
    items: INetworkEmailLogListItem[];
    total: number;
}

const useNetworkEmailLogFilteredList = (request: INetworkEmailLogFilteredListRequest): IRetVal => {
    const { sender, search, centerId, sortBy, sortDescending, page, pageSize } = request;

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [items, setItems] = useState<INetworkEmailLogListItem[]>([]);
    const [total, setTotal] = useState<number>(0);

    const { dispatch } = useAppHookContext();

    useEffect(() => {
        setLoading(true);
        setError('');

        const source = axios.CancelToken.source();

        const fetchData = async () => {
            const params = new URLSearchParams({
                page: String(page ?? 1),
                pageSize: String(pageSize ?? 25),
                sortDescending: String(sortDescending ?? true)
            });

            if (sender) {
                params.append('sender', sender);
            }

            if (search) {
                params.append('search', search);
            }

            if (centerId) {
                params.append('centerId', String(centerId));
            }

            if (sortBy) {
                params.append('sortBy', sortBy);
            }

            const config: IGet = {
                url: `${__API__}/email/network/log/filtered-list?${params.toString()}`,
                cancelToken: source.token,
            };

            let cancelled = false;
            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.success) {
                    setItems(response.data.results.items);
                    setTotal(response.data.results.total);
                } else {
                    setError('Unable to load list');
                    setItems([]);
                    setTotal(0);
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    cancelled = true;
                    return;
                }
                setItems([]);
                setTotal(0);
                setError(err?.message ?? 'An error occurred');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => source.cancel();
    }, [sender, search, centerId, sortBy, sortDescending, page, pageSize]);

    return { loading, error, items, total };
};

export default useNetworkEmailLogFilteredList;
